import { useState } from "react";
import { Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { useCoachNotifications, useMarkNotificationOpen } from "@/lib/coachQueries";
import { useNavigate } from "react-router-dom";

/**
 * Master v5 Prompt 16 — topbar notifications bell. Aggregates the
 * EXISTING per-Workspace Notification model across every Workspace
 * this coach coaches (see coach_task_routes.py's /coach/notifications)
 * — reuses the existing per-Workspace "open" resolution route rather
 * than duplicating its target-resolution logic.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const { data } = useCoachNotifications();
  const unread = data?.unread_count || 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-full hover:bg-ink/5 transition-colors text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-terracotta"
        aria-label="Notifications"
        data-testid="coach-notifications-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-paper text-[9px] font-semibold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="coach-notifications-unread-badge">
            {unread}
          </span>
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coach-notifications-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Notifications</DialogTitle>
            <DialogDescription>Across every Workspace you coach.</DialogDescription>
          </DialogHeader>
          <NotificationsList onNavigate={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function NotificationsList({ onNavigate }) {
  const { data, isPending } = useCoachNotifications();
  const navigate = useNavigate();
  const openNotification = useMarkNotificationOpen();
  const items = data?.items || [];

  async function handleOpen(n) {
    try {
      const result = await openNotification.mutateAsync({ workspaceId: n.workspace_id, ntfId: n.ntf_id });
      onNavigate();
      if (result.route === "messages") {
        navigate(`/rehearsal-room/w/${n.workspace_id}/messages`);
      } else if (result.route === "room" && result.launch_id) {
        navigate(`/rehearsal-room/w/${n.workspace_id}/room/${result.launch_id}`);
      } else if (result.feedback_id) {
        navigate(`/rehearsal-room/w/${n.workspace_id}/feedback/${result.feedback_id}`);
      } else {
        navigate(`/rehearsal-room/w/${n.workspace_id}/home`);
      }
    } catch {
      // A revoked/expired target returns a non-enumerating 404 — nothing
      // to navigate to; the notification stays in the list, unread.
    }
  }

  if (isPending) return <p className="text-sm text-ink-soft" data-testid="coach-notifications-loading">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-ink-soft" data-testid="coach-notifications-empty">No notifications yet.</p>;

  return (
    <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="coach-notifications-list">
      {items.map((n) => (
        <button
          key={n.ntf_id}
          onClick={() => handleOpen(n)}
          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${n.read_at ? "text-ink-soft hover:bg-ink/5" : "font-medium text-ink bg-terracotta/5 hover:bg-terracotta/10"}`}
          data-testid={`coach-notification-item-${n.ntf_id}`}
        >
          {n.title}
          <span className="block text-[11px] text-ink-soft font-normal mt-0.5">{new Date(n.created_at).toLocaleString()}</span>
        </button>
      ))}
    </div>
  );
}
